(function () {
  'use strict';

  /*
    Mural Wonder / Apippa pricing bridge.
    Production-safe opt-in scope: this script is loaded only for products carrying
    the Shopify product tag `mw-apippa-enabled`. The cropper also exposes a matching
    data flag, and the bridge refuses to initialize unless that flag is present.

    The Mural Wonder cropper remains customer-facing while Apippa stays mounted
    off-screen as the verified server-side pricing engine.
  */
  var DISCOVERY_TIMEOUT_MS = 12000;
  var DISCOVERY_INTERVAL_MS = 250;

  function normalize(value) {
    return String(value || '')
      .replace(/&amp;/gi, '&')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function isOutsideCropper(element, cropper) {
    return element && cropper && !cropper.contains(element);
  }

  function optionLabels(select) {
    return Array.prototype.map.call(select.options || [], function (option) {
      return normalize(option.textContent || option.label || option.value);
    });
  }

  function hasAllOptions(select, expected) {
    var labels = optionLabels(select);
    return expected.every(function (item) {
      return labels.indexOf(normalize(item)) !== -1;
    });
  }

  function findSelectByOptions(cropper, expected) {
    var selects = Array.prototype.slice.call(document.querySelectorAll('select'));
    for (var i = 0; i < selects.length; i += 1) {
      if (!isOutsideCropper(selects[i], cropper)) continue;
      if (hasAllOptions(selects[i], expected)) return selects[i];
    }
    return null;
  }

  function commonAncestor(a, b) {
    if (!a || !b) return null;
    var node = a;
    while (node && node !== document.documentElement) {
      if (node.contains(b)) return node;
      node = node.parentElement;
    }
    return document.body;
  }

  function directNearbyText(input) {
    var parts = [];
    if (input.getAttribute('aria-label')) parts.push(input.getAttribute('aria-label'));
    if (input.getAttribute('name')) parts.push(input.getAttribute('name'));

    if (input.id) {
      try {
        var label = document.querySelector('label[for="' + CSS.escape(input.id) + '"]');
        if (label) parts.push(label.textContent || '');
      } catch (error) {
        /* CSS.escape is not critical; continue with nearby text. */
      }
    }

    if (input.labels && input.labels.length) {
      Array.prototype.forEach.call(input.labels, function (label) {
        parts.push(label.textContent || '');
      });
    }

    var previous = input.previousElementSibling;
    if (previous) parts.push(previous.textContent || '');

    var parent = input.parentElement;
    if (parent) {
      Array.prototype.forEach.call(parent.children, function (child) {
        if (child !== input && !/^(INPUT|SELECT|TEXTAREA|BUTTON)$/i.test(child.tagName)) {
          parts.push(child.textContent || '');
        }
      });
    }

    return normalize(parts.join(' '));
  }

  function labelScore(input, label) {
    var wanted = normalize(label);
    var nearby = directNearbyText(input);
    if (nearby === wanted) return 100;
    if (nearby.indexOf(wanted) !== -1) return 80;

    var node = input.parentElement;
    var depth = 0;
    while (node && depth < 4) {
      var text = normalize(node.textContent || '');
      if (text === wanted) return 60 - depth;
      if (text.indexOf(wanted) !== -1) return 40 - depth;
      node = node.parentElement;
      depth += 1;
    }
    return 0;
  }

  function findNumberInput(container, cropper, label, exclude) {
    var scope = container || document;
    var inputs = Array.prototype.slice.call(scope.querySelectorAll('input[type="number"], input[inputmode="decimal"], input[inputmode="numeric"]'));
    var best = null;
    var bestScore = -1;

    inputs.forEach(function (input) {
      if (!isOutsideCropper(input, cropper)) return;
      if (exclude && exclude.indexOf(input) !== -1) return;
      var score = labelScore(input, label);
      if (score > bestScore) {
        best = input;
        bestScore = score;
      }
    });

    return bestScore > 0 ? best : null;
  }

  function findApippaControls(cropper) {
    var unit = findSelectByOptions(cropper, ['cm', 'in', 'ft']);
    var material = findSelectByOptions(cropper, ['premium', 'washable', 'peel & stick']);
    if (!unit || !material) return null;

    var container = commonAncestor(unit, material) || document;
    var width = findNumberInput(container, cropper, 'width', []);
    var height = findNumberInput(container, cropper, 'height', width ? [width] : []);

    /* Fallback: if Apippa's labels are not associated semantically, use the two
       numeric inputs inside the common calculator container in document order. */
    if (!width || !height) {
      var candidates = Array.prototype.slice.call(container.querySelectorAll('input[type="number"], input[inputmode="decimal"], input[inputmode="numeric"]'))
        .filter(function (input) { return isOutsideCropper(input, cropper); });
      if (candidates.length >= 2) {
        width = width || candidates[0];
        height = height || candidates.filter(function (input) { return input !== width; })[0];
      }
    }

    if (!width || !height) return null;

    return {
      container: container,
      width: width,
      height: height,
      unit: unit,
      material: material
    };
  }

  function setNativeValue(element, value) {
    if (!element) return;
    var prototype = element.tagName === 'SELECT' ? window.HTMLSelectElement.prototype : window.HTMLInputElement.prototype;
    var descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');
    if (descriptor && descriptor.set) descriptor.set.call(element, String(value));
    else element.value = String(value);
  }

  function dispatchUpdate(element) {
    if (!element) return;
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function setSelectByVisibleLabel(select, label) {
    if (!select) return false;
    var wanted = normalize(label);
    var option = Array.prototype.find.call(select.options || [], function (item) {
      return normalize(item.textContent || item.label || item.value) === wanted;
    });
    if (!option) return false;
    setNativeValue(select, option.value);
    dispatchUpdate(select);
    return true;
  }

  function materialFromCard(card) {
    var key = normalize(card && card.dataset ? card.dataset.mwRateCard : '');
    if (key === 'washable') return 'Washable';
    if (key === 'peel-stick') return 'Peel & Stick';
    return 'Premium';
  }

  function applyCropperMaterial(cropper, material) {
    var instance = cropper._mwCropperInstance;
    if (!instance || !instance.state) return;

    material = material === 'Washable' || material === 'Peel & Stick' ? material : 'Premium';
    var previousMaterial = instance.state.material;
    if (previousMaterial !== material && instance.state.ready && typeof instance.invalidateConfirmation === 'function') {
      instance.invalidateConfirmation('material');
    }
    instance.state.material = material;
    instance.state.rate = instance.rates[material] || instance.rates.Premium || 27;
    instance.state.minimum = instance.minimums[material] || instance.minimums.Premium || 30;

    if (instance.priceLabel) {
      instance.priceLabel.textContent = material + ' · £' + instance.state.rate + ' / m²';
    }

    instance.rateCards.forEach(function (card) {
      var active = materialFromCard(card) === material;
      card.classList.toggle('is-active', active);
      card.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (typeof instance.updatePriceFromInputs === 'function') instance.updatePriceFromInputs();
    if (instance.state.ready && !instance.state.dirty) {
      if (typeof instance.updateSummary === 'function') instance.updateSummary();
      if (typeof instance.schedulePropertiesUpdate === 'function') instance.schedulePropertiesUpdate();
    }
  }

  function currentCropperMaterial(cropper) {
    var instance = cropper._mwCropperInstance;
    if (instance && instance.state && instance.state.material) return instance.state.material;
    var active = cropper.querySelector('[data-mw-rate-card].is-active');
    return materialFromCard(active);
  }

  function syncToApippa(cropper, controls) {
    if (!cropper || !controls) return;
    var width = cropper.querySelector('[data-mw-width]');
    var height = cropper.querySelector('[data-mw-height]');
    var unit = cropper.querySelector('[data-mw-unit]');
    if (!width || !height || !unit) return;

    /* Selects first so Apippa calculates the numeric values with the right context. */
    setSelectByVisibleLabel(controls.unit, unit.value);
    setSelectByVisibleLabel(controls.material, currentCropperMaterial(cropper));

    setNativeValue(controls.width, width.value || '0');
    dispatchUpdate(controls.width);
    setNativeValue(controls.height, height.value || '0');
    dispatchUpdate(controls.height);

    cropper.dataset.mwApippaBridge = 'connected';
  }

  function makeRateCardsInteractive(cropper, controls) {
    var cards = Array.prototype.slice.call(cropper.querySelectorAll('[data-mw-rate-card]'));
    cards.forEach(function (card) {
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-pressed', card.classList.contains('is-active') ? 'true' : 'false');
      card.style.cursor = 'pointer';

      function choose() {
        applyCropperMaterial(cropper, materialFromCard(card));
        syncToApippa(cropper, controls);
      }

      card.addEventListener('click', choose);
      card.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          choose();
        }
      });
    });
  }

  function bindCropper(cropper, controls) {
    if (cropper.dataset.mwApippaBridgeBound === 'true') return;
    cropper.dataset.mwApippaBridgeBound = 'true';

    var width = cropper.querySelector('[data-mw-width]');
    var height = cropper.querySelector('[data-mw-height]');
    var unit = cropper.querySelector('[data-mw-unit]');

    function sync() {
      window.requestAnimationFrame(function () {
        syncToApippa(cropper, controls);
      });
    }

    [width, height].forEach(function (input) {
      if (!input) return;
      input.addEventListener('input', sync);
      input.addEventListener('change', sync);
    });
    if (unit) unit.addEventListener('change', sync);

    makeRateCardsInteractive(cropper, controls);
    syncToApippa(cropper, controls);
  }

  function visuallyHideApippaEngine(cropper, controls) {
    var container = controls && controls.container;

    /* Fail safe: never hide a broad page container or anything containing our cropper. */
    if (!container ||
        container === document.body ||
        container === document.documentElement ||
        container.contains(cropper) ||
        !container.contains(controls.width) ||
        !container.contains(controls.height) ||
        !container.contains(controls.unit) ||
        !container.contains(controls.material)) {
      cropper.dataset.mwApippaEngine = 'hide-skipped';
      return false;
    }

    if (container.dataset.mwApippaEngineHidden === 'true') {
      cropper.dataset.mwApippaEngine = 'hidden';
      return true;
    }

    container.dataset.mwApippaEngineHidden = 'true';
    container.setAttribute('aria-hidden', 'true');

    /* Keep Apippa mounted and measurable by its own JavaScript, but remove the
       duplicate customer-facing UI from layout. We intentionally avoid
       display:none so the already-verified pricing engine remains active. */
    var style = container.style;
    style.position = 'absolute';
    style.width = '1px';
    style.height = '1px';
    style.padding = '0';
    style.margin = '-1px';
    style.overflow = 'hidden';
    style.clip = 'rect(0 0 0 0)';
    style.clipPath = 'inset(50%)';
    style.whiteSpace = 'nowrap';
    style.border = '0';
    style.opacity = '0';
    style.pointerEvents = 'none';

    Array.prototype.forEach.call(
      container.querySelectorAll('input, select, textarea, button, a[href], [tabindex]'),
      function (element) {
        element.setAttribute('tabindex', '-1');
      }
    );

    cropper.dataset.mwApippaEngine = 'hidden';
    return true;
  }

  function isEligibleCropper(cropper) {
    return !!cropper && normalize(cropper.dataset.mwApippaEnabled || '') === 'true';
  }

  function tryInit(cropper, startedAt) {
    if (!isEligibleCropper(cropper)) return;
    if (!cropper._mwCropperInstance) {
      if (Date.now() - startedAt < DISCOVERY_TIMEOUT_MS) {
        window.setTimeout(function () { tryInit(cropper, startedAt); }, DISCOVERY_INTERVAL_MS);
      }
      return;
    }

    var controls = findApippaControls(cropper);
    if (controls) {
      bindCropper(cropper, controls);
      visuallyHideApippaEngine(cropper, controls);
      return;
    }

    cropper.dataset.mwApippaBridge = 'waiting';
    if (Date.now() - startedAt < DISCOVERY_TIMEOUT_MS) {
      window.setTimeout(function () { tryInit(cropper, startedAt); }, DISCOVERY_INTERVAL_MS);
    } else {
      cropper.dataset.mwApippaBridge = 'not-found';
      if (window.console && console.warn) console.warn('[Mural Wonder] Apippa controls were not found; no pricing fields were modified.');
    }
  }

  function init(scope) {
    var root = scope || document;
    var croppers = [];
    if (root.matches && root.matches('[data-mural-cropper]')) croppers.push(root);
    if (root.querySelectorAll) {
      croppers = croppers.concat(Array.prototype.slice.call(root.querySelectorAll('[data-mural-cropper]')));
    }
    croppers.forEach(function (cropper) {
      tryInit(cropper, Date.now());
    });
  }

  window.MuralWonderApippaBridge = {
    version: '7C3-tag-gated-hidden-engine-1.0.0',
    init: init
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
